import { useState } from 'react';
import { Switch, Typography } from 'antd';
import { TagOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ProviderService } from '../../../types/provider';

const { Text } = Typography;

interface ServiceRowProps {
  service: ProviderService;
  displayName: string;
  bookingModeLabel: string;
  onEdit: (service: ProviderService) => void;
  onToggle: (service: ProviderService) => void;
}

const AVATAR_PALETTE: { fg: string; bgFrom: string; bgTo: string; border: string }[] = [
  { fg: '#3390ec', bgFrom: 'rgba(51,144,236,0.12)',  bgTo: 'rgba(51,144,236,0.26)',  border: 'rgba(51,144,236,0.18)'  },
  { fg: '#1f8b4c', bgFrom: 'rgba(31,139,76,0.12)',   bgTo: 'rgba(31,139,76,0.26)',   border: 'rgba(31,139,76,0.18)'   },
  { fg: '#fa8c16', bgFrom: 'rgba(250,140,22,0.12)',  bgTo: 'rgba(250,140,22,0.26)',  border: 'rgba(250,140,22,0.18)'  },
  { fg: '#9254de', bgFrom: 'rgba(146,84,222,0.12)',  bgTo: 'rgba(146,84,222,0.26)',  border: 'rgba(146,84,222,0.18)'  },
  { fg: '#eb2f96', bgFrom: 'rgba(235,47,150,0.12)',  bgTo: 'rgba(235,47,150,0.26)',  border: 'rgba(235,47,150,0.18)'  },
];

function pickPalette(id: number) {
  return AVATAR_PALETTE[Math.abs(id) % AVATAR_PALETTE.length];
}

/**
 * Xizmat kartasi — butun karta bosiluvchan (edit ochadi).
 * Switch propagation to'xtatadi — alohida toggle harakati.
 */
export function ServiceRow({
  service,
  displayName,
  bookingModeLabel,
  onEdit,
  onToggle,
}: ServiceRowProps) {
  const { t } = useTranslation(['common', 'provider']);
  const [pressed, setPressed] = useState(false);
  const palette = pickPalette(service.id);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onEdit(service)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit(service);
        }
      }}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      onTouchCancel={() => setPressed(false)}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onMouseLeave={() => setPressed(false)}
      style={{
        borderRadius: 14,
        background: 'var(--bl-surface, #fff)',
        border: '1px solid var(--bl-divider, rgba(16,24,40,0.08))',
        boxShadow: pressed
          ? '0 1px 4px rgba(0,0,0,0.04)'
          : 'var(--bl-shadow-soft, 0 2px 8px rgba(0,0,0,0.04))',
        cursor: 'pointer',
        transform: pressed ? 'scale(0.99)' : 'scale(1)',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        WebkitTapHighlightColor: 'transparent',
        outline: 'none',
      }}
    >
      <div
        style={{
          padding: '12px 12px 12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {/* Avatar */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 11,
            background: `linear-gradient(135deg, ${palette.bgFrom} 0%, ${palette.bgTo} 100%)`,
            border: `1px solid ${palette.border}`,
            color: palette.fg,
            fontSize: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <TagOutlined />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--bl-text-strong, #0f172a)',
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: -0.1,
              marginBottom: 3,
            }}
          >
            {displayName}
          </Text>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              fontSize: 11,
              color: 'var(--bl-text-muted, #6b7280)',
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <ClockCircleOutlined style={{ fontSize: 10, opacity: 0.7 }} />
              {t('common:units.minutesShort', { count: service.durationMinutes })}
            </span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span
              className="bl-tabular-nums"
              style={{
                fontWeight: 700,
                color: 'var(--bl-text-strong, #0f172a)',
              }}
            >
              {service.price.toLocaleString('uz-UZ')}{' '}
              <span style={{ fontWeight: 500, color: 'var(--bl-text-muted, #9ca3af)' }}>
                {t('provider:common.currency')}
              </span>
            </span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ fontWeight: 500 }}>{bookingModeLabel}</span>
          </div>
        </div>

        {/* Switch — propagation to'xtatadi (kartochka bosilmasin) */}
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{ flexShrink: 0 }}
        >
          <Switch checked={service.active} onChange={() => onToggle(service)} size="small" />
        </div>
      </div>
    </div>
  );
}
